<template>
    <v-container fluid>
        <PageTitle>{{ t('common.dashboard.title') }}</PageTitle>
        <v-row v-if="dashboardItems.length > 0">
            <template v-for="widget in dashboardItems" :key="widget.id">
                <DashboardWidgetLoader v-if="widget.loading" :size="widgetLayoutSize(widget)" :widget="widget" />
                <DashboardWidgetError v-else-if="widget.loadFailed" :size="widgetLayoutSize(widget)" :widget="widget" />
                <component :is="resolveWidgetComponent(widget)" v-else :permissions="userPermissions" :widget="widget" />
            </template>
        </v-row>
        <v-row v-else-if="showNoWidgetsMessage">
            <v-col cols="12">
                <v-alert type="info" variant="tonal">{{ t('common.dashboard.labels.noWidgets') }}</v-alert>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import type { Component } from 'vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import PageTitle from '@/components/common/PageTitle.vue'
import { errorHandler } from '@/composables/errorHandler'
import { useCommonStore, useDashboardStore, useUserStore } from '@/stores'
import type { DashboardWidget } from '@/stores/dashboard'
import {
    DASHBOARD_MACHINE_REVIEW_CODE_NAME,
    getDashboardItemInitialQuery,
    isDashboardWidgetLarge
} from '@/views/Dashboard/dashboardConstants'
import { normalizeWidgetKey } from '@/views/Dashboard/dashboardUtils'

import AccidentsDividedPerDepartment from './Widgets/AccidentsDividedPerDepartment.vue'
import DashboardWidgetError from './Widgets/DashboardWidgetError.vue'
import DashboardWidgetLoader from './Widgets/DashboardWidgetLoader.vue'
import FourMonthsComplaintsReport from './Widgets/FourMonthsComplaintsReport.vue'
import NewOrders from './Widgets/NewOrders.vue'
import NumberOfOrdersPerImposition from './Widgets/NumberOfOrdersPerImposition.vue'
import OrdersDelayedReport from './Widgets/OrdersDelayedReport.vue'
import OrdersPerDayNotSentReport from './Widgets/OrdersPerDayNotSentReport.vue'
import OrdersProcesses from './Widgets/OrdersProcesses.vue'
import OrdersSentDailyByShipperReport from './Widgets/OrdersSentDailyByShipperReport.vue'
import PaperNeeded from './Widgets/PaperNeeded.vue'
import ProcessedOrders from './Widgets/ProcessedOrders.vue'
import QualitativeNotes from './Widgets/QualitativeNotes.vue'
import SimpleCount from './Widgets/SimpleCount.vue'
import StopCardsCount from './Widgets/StopCardsCount.vue'
import ThreeMonthsReport from './Widgets/ThreeMonthsReport.vue'
import ThreeMonthsTotalReport from './Widgets/ThreeMonthsTotalReport.vue'
import UnsupportedWidget from './Widgets/UnsupportedWidget.vue'
import UserTasksList from './Widgets/UserTasksList.vue'

// 3. Constants
const widgetComponentMap: Record<string, Component> = {
    AccidentsDividedPerDepartment,
    FourMonthsComplaintsReport,
    NewOrders,
    NumberOfOrdersPerImposition,
    OrdersDelayedReport,
    OrdersPerDayNotSentReport,
    OrdersProcesses,
    OrdersSentDailyByShipperReport,
    PaperNeeded,
    ProcessedOrders,
    QualitativeNotes,
    SimpleCount,
    StopCardsCount,
    ThreeMonthsReport,
    ThreeMonthsTotalReport,
    UserTasksList
}

const widgetCodeNameMap: Record<string, Component> = {
    complaintsDividedPerDepartment: AccidentsDividedPerDepartment,
    notClosedAccidents: AccidentsDividedPerDepartment,
    fourMonthsComplaintsReport: FourMonthsComplaintsReport,
    myQualitativeNotes: QualitativeNotes,
    newOrders: NewOrders,
    numberOfOrdersPerImposition: NumberOfOrdersPerImposition,
    ordersDelayedReport: OrdersDelayedReport,
    ordersPerDayNotSent: OrdersPerDayNotSentReport,
    ordersProcesses: OrdersProcesses,
    ordersProductsDelayed: OrdersDelayedReport,
    ordersProductsPerDayNotSent: OrdersPerDayNotSentReport,
    ordersSentDailyByShipper: OrdersSentDailyByShipperReport,
    paperNeededReport: PaperNeeded,
    processedOrders: ProcessedOrders,
    stopCardsCount: StopCardsCount,
    userTasksNotFinished: UserTasksList
}

const normalizedTypeMap: Record<string, Component> = Object.fromEntries(
    Object.entries(widgetComponentMap).map(([key, component]) => [normalizeWidgetKey(key), component])
)

// 4. Stores & top-level services
const commonStore = useCommonStore()
const dashboardStore = useDashboardStore()
const userStore = useUserStore()
const { t } = useI18n()

// 5. Reactive state (refs/reactive)
const dashboardInitialLoadFinished = ref(false)
let isActive = true

// 7. Computed
const dashboardItems = computed(() => dashboardStore.dashboardItems || [])
const userPermissions = computed(() => userStore.permissions || [])
const showNoWidgetsMessage = computed(() => dashboardInitialLoadFinished.value && dashboardItems.value.length === 0)

// 8. Handlers / methods
function resolveWidgetComponent(widget: DashboardWidget | undefined): Component {
    const codeNameKey = normalizeWidgetKey(widget?.codeName)
    const typeKey = normalizeWidgetKey(widget?.type)

    if (codeNameKey && widgetCodeNameMap[codeNameKey]) {
        return widgetCodeNameMap[codeNameKey]
    }

    if (typeKey && normalizedTypeMap[typeKey]) {
        return normalizedTypeMap[typeKey]
    }

    if (widget?.body?.count !== undefined) {
        return SimpleCount
    }

    return UnsupportedWidget
}

function widgetLayoutSize(widget: DashboardWidget | undefined) {
    return isDashboardWidgetLarge(widget) ? 'large' : 'small'
}

async function loadDashboardItem(item: DashboardWidget) {
    if (!isActive) {
        return
    }

    await dashboardStore.getDashboardItem({
        item,
        paginate: '',
        query: getDashboardItemInitialQuery(item?.codeName)
    })
}

async function initializeDashboard() {
    commonStore.toggleMainLoader(true)

    try {
        await dashboardStore.getDashboardSections()
        if (!isActive) {
            return
        }

        dashboardStore.clearDashboardItems()
        const response = await dashboardStore.getDashboardItems()
        if (!isActive) {
            return
        }

        const visibleItems = (response?.data?.results || []).filter(
            (item: DashboardWidget) => item?.codeName !== DASHBOARD_MACHINE_REVIEW_CODE_NAME
        )

        dashboardStore.initializeDashboardItems(visibleItems)

        commonStore.toggleMainLoader(false)

        void Promise.allSettled(visibleItems.map((item: DashboardWidget) => loadDashboardItem(item)))
    } catch (error) {
        if (isActive) {
            errorHandler(error)
        }
    } finally {
        // Always release the loader this component turned on, even after unmount.
        commonStore.toggleMainLoader(false)
        if (isActive) {
            dashboardInitialLoadFinished.value = true
        }
    }
}

// 9. Helpers / lifecycle
onMounted(async () => {
    await initializeDashboard()
})

onUnmounted(() => {
    isActive = false
})
</script>
